import { ProjectAnalysisTab } from "@/types/projectAnalysisTab";
import { FeatureManagementType, ImportModelEtlJobsRequest, JobsRequest, Permissions, Project, RebarEstimationStatus } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useEtlJobs } from "@api/etlJobs";
import { useJobs } from "@api/jobs";
import { useCreateProjectImage, useProject } from "@api/projects";
import Breadcrumbs from "@components/Breadcrumbs/Breadcrumbs";
import FullPageLoading from "@components/FullPageLoading";
import ProjectStepper from "@components/ProjectStepper/ProjectStepper";
import useProjectAnalysisTabs from "@features/analysis/hooks/useProjectAnalysisTabs";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Card, CardContent, CardHeader, Grid, IconButton, Stack, Switch, Tooltip, Typography, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AddModelsForm from "../AddModelsForm";
import ProjectGrid from "../ProjectGrid";
import { ProjectSettingsDialog } from "./ProjectSettingsDialog";

const ProjectPage = observer(() => {
  const theme = useTheme();
  const [openNewModelsDialog, setOpenNewProjectDialog] = useState<boolean>(false);
  const [openRebarDialog, setOpenRebarDialog] = useState<boolean>(false);
  const [hasSelectedModels, setHasSelectedModels] = useState<boolean>(false);
  const { id: projectId } = useParams();
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const navigate = useNavigate();
  const upStore = useUserPermissionStore();
  const project = useProject(projectId!);
  const { isPending: isProjectImageLoading, mutateAsync: createProjectImageAsync } = useCreateProjectImage(projectId!);
  const inputEl = useRef<any>(null);
  const projectFormRef = useRef<any>();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const appSettings = useAppSettings();
  const projectAnalysisTabs = useProjectAnalysisTabs();
  const jobRequest = new JobsRequest({ projectIds: [projectId!] });
  const jobs = useJobs(jobRequest, { enabled: !!projectId });
  const importModelEtlJobsRequest = new ImportModelEtlJobsRequest({ projectIds: [projectId!] });
  const etlJobs = useEtlJobs(importModelEtlJobsRequest, { enabled: !!projectId });

  console.log("ETL Jobs:", etlJobs.data);

  useEffect(() => {
    selectionManager.setProject(project.data?.project);
    upStore.setResourcePermissions(project.data?.permissions);
  }, [project.data]);

  const handleSelectFile = async (event: any) => {
    await createProjectImageAsync({ data: event.target.files[0], fileName: event.target.files[0].name } as any);
  };

  const handleCloseForm = () => {
    setOpenNewProjectDialog(false);

    setHasSelectedModels(false);
  };

  const handleModelSelectionChange = (hasSelection: boolean) => {
    setHasSelectedModels(hasSelection);
  };

  const handleCreateScenario = () => {
    navigate(`/projects/${selectionManager.selectedProject?.projectId}/analysis#${projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Scenarios)}`);
  };

  const handleAssignMaterials = () => {
    navigate(`/projects/${selectionManager.selectedProject?.projectId}/analysis#${projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Assign)}`);
  };

  const handleViewResults = () => {
    navigate(`/projects/${selectionManager.selectedProject?.projectId}/analysis#${projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Results)}`);
  };

  const handleViewProjectSettings = () => {
    setOpenRebarDialog(true);
  };

  const handleCloseRebarSettings = () => {
    setOpenRebarDialog(false);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitted(true);
      projectFormRef.current?.onSubmit();
    } finally {
      setIsSubmitted(false);
    }
  };

  if (project.isPending) {
    return <FullPageLoading />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, flexShrink: 1 }}>
      <Box sx={{ height: "100%", width: "100vw" }}>
        <Container maxWidth={false}>
          <Grid mt={1} container spacing={2}>
            <Grid size={12}>
              <ProjectStepper />
              {false && <Breadcrumbs />}
              <Box mt={2} sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h5">
                  {i18n.t("project-page.title")} {selectionManager.selectedProject?.name}
                </Typography>
                <Box sx={{ flex: 1, paddingLeft: "20px" }}>
                  {selectionManager.selectedProject?.apsProjectName ? i18n.t("project-page.acc-title") + " : " : ""}{" "}
                  <Typography variant="caption">{selectionManager.selectedProject?.apsProjectName}</Typography>
                  <Tooltip title={i18n.t("project-page.acc-info-tooltip")}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, verticalAlign: "middle", marginLeft: "6px", color: theme.palette.primary.main }} />
                  </Tooltip>
                </Box>
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "end" }}>
                    <Stack direction="row" spacing={2}>
                      {
                        <Tooltip
                          title={
                            appSettings.data?.rebarEstimation ? i18n.t("project-page.project-setting-button.tooltip.1") : i18n.t("project-page.project-setting-button.tooltip.2")
                          }
                        >
                          <span>
                            <Button
                              variant="contained"
                              onClick={handleViewProjectSettings}
                              disabled={
                                !appSettings.data?.profileLevelRebarSettings &&
                                !(upStore.hasPermission(Permissions.App_Project_Settings) && (appSettings.data?.rebarEstimation || appSettings.data?.additionalMetadata))
                              }
                            >
                              {i18n.t("project-page.project-setting-button.title")}
                            </Button>
                          </span>
                        </Tooltip>
                      }
                      <Tooltip
                        title={
                          selectionManager.selectedProject?.hasModels ? i18n.t("project-page.add-models-button.tooltip.1") : i18n.t("project-page.add-models-button.tooltip.2")
                        }
                      >
                        <span>
                          <Button variant="contained" onClick={() => setOpenNewProjectDialog(true)} disabled={!upStore.hasPermission(Permissions.App_Project_Model_Create)}>
                            {i18n.t("project-page.add-models-button.title")}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={selectionManager.selectedProject?.hasModels ? i18n.t("project-page.scenarios-button.tooltip.1") : i18n.t("project-page.scenarios-button.tooltip.2")}
                      >
                        <span>
                          <Button
                            variant="contained"
                            onClick={handleCreateScenario}
                            disabled={!selectionManager.selectedProject?.hasModels || !upStore.hasPermission(Permissions.App_Project_Scenario_View)}
                          >
                            {i18n.t("project-page.scenarios-button.title")}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          selectionManager.selectedProject?.hasModels
                            ? i18n.t("project-page.assign-materials-button.tooltip.1")
                            : i18n.t("project-page.assign-materials-button.tooltip.2")
                        }
                      >
                        <span>
                          <Button
                            variant="contained"
                            onClick={handleAssignMaterials}
                            disabled={!selectionManager.selectedProject?.hasModels || !upStore.hasPermission(Permissions.App_Project_Model_Materials_View)}
                          >
                            {i18n.t("project-page.assign-materials-button.title")}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          selectionManager.selectedProject?.hasModels ? i18n.t("project-page.view-results-button.tooltip.1") : i18n.t("project-page.view-results-button.tooltip.2")
                        }
                      >
                        <span>
                          <Button
                            variant="contained"
                            onClick={handleViewResults}
                            disabled={!selectionManager.selectedProject?.hasModels || !upStore.hasPermission(Permissions.App_Project_Results_View)}
                          >
                            {i18n.t("project-page.view-results-button.title")}
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid size={12}>
              <Card sx={{ width: "100%", mb: 2 }}>
                <ProjectGrid jobs={jobs.data} etlJobs={etlJobs.data} project={project.data?.project as Project} />
              </Card>
            </Grid>
            <Grid size={6}>
              {selectionManager.selectedProject?.imageUrl && (
                <Box>
                  <img src={selectionManager.selectedProject?.imageUrl} width="500px" height="270px" alt="" />
                </Box>
              )}
              <Box>
                <label htmlFor="btn-upload">
                  <input hidden id="btn-upload" name="btn-upload" type="file" onChange={handleSelectFile} ref={inputEl} />
                  <Button loading={isProjectImageLoading} component="span" variant="contained">
                    {selectionManager.selectedProject?.imageUrl ? i18n.t("project-page.image-button.title.1") : i18n.t("project-page.image-button.title.2")}
                  </Button>
                </label>
              </Box>
            </Grid>
            {false && appStore.hasFeature(FeatureManagementType.RebarEstimation) && (
              <Grid size={6}>
                <Card elevation={3} sx={{ "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default } }}>
                  <CardHeader title={<Typography variant="body2">{i18n.t("project-page.project-details.header")}</Typography>} elevation={3} />
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="body1">{i18n.t("project-page.project-details.title.rebar-estimation")}</Typography>
                        <Typography variant="subtitle1" color={theme.palette.grey[500]} fontSize="0.75rem">
                          {i18n.t("project-page.project-details.description.rebar-estimation")}
                        </Typography>
                      </Box>
                      <Switch
                        disabled={true}
                        checked={
                          selectionManager.selectedProject?.rebarEstimationStatus === RebarEstimationStatus.Enabled ||
                          selectionManager.selectedProject?.rebarEstimationStatus === RebarEstimationStatus.Estimated
                        }
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Container>
        <Dialog
          fullWidth={true}
          maxWidth={"xl"}
          open={openNewModelsDialog}
          onClose={handleCloseForm}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            sx: {
              height: "80%",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {i18n.t("project-page.add-models-dialog.title")}
            <IconButton title={i18n.t("project-page.close-button.title")} onClick={handleCloseForm}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ height: "100%", paddingBottom: 0 }}>
            <AddModelsForm
              ref={projectFormRef}
              handleCloseDialog={() => handleCloseForm()}
              carbonToolProjectId={selectionManager.selectedProject?.projectId}
              onSelectionChange={handleModelSelectionChange}
            />
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleCloseForm}>
              {i18n.t("project-page.add-models-dialog.close-button.title")}
            </Button>
            <Button loading={isSubmitted} variant="contained" onClick={handleSubmit} disabled={!hasSelectedModels}>
              {i18n.t("project-page.add-models-dialog.add-models-button.title")}
            </Button>
          </DialogActions>
        </Dialog>
        <ProjectSettingsDialog
          open={openRebarDialog}
          onClose={handleCloseRebarSettings}
          project={project.data || undefined}
          projectId={selectionManager.selectedProject?.projectId || ""}
        />
      </Box>
    </Box>
  );
});

export default ProjectPage;
